import React, { useState } from 'react'
import styles from './table.module.css'
import { useGradesData, StudentRow, GradeCell } from './context'
import { SlotPercent } from 'util/grades-grid'
import { useModalBehaviour } from 'util/use-modal-behaviour'

export type ViewMode = 'grid' | 'list'

const TIER_CLASS: Record<string, string> = {
	Emerging: styles.tEmerging,
	Intermediate: styles.tIntermediate,
	Advanced: styles.tAdvanced,
}

const TierTag = ({ tier }: { tier: string }) => (
	<span className={`${styles.tierTag} ${TIER_CLASS[tier] || ''}`}>
		<span className={styles.tierDot} />
		{tier}
	</span>
)

// Holds only the lookup key, not a snapshot of the row -- so the modal
// always reads the current cell out of the live `rows` prop instead of a
// stale copy from the moment it was opened (matters once marking a cell
// triggers a refetch: the same target should show the fresh grade).
type GradeTarget = { studentId: string; slotIndex: number }

/* A cell only reaches here once it has an AI grade (correct/incorrect) --
   this app grades every submission immediately, so there is no "submitted
   but ungraded" limbo state the mockup's grid assumes. "Next ungraded"
   is grounded in the state that actually exists in our data: the next
   graded answer a teacher hasn't manually reviewed/confirmed yet. */
const findFirstUnreviewed = (rows: StudentRow[], excluding?: GradeTarget): GradeTarget | null => {
	for (const row of rows) {
		for (let slotIndex = 0; slotIndex < row.cells.length; slotIndex++) {
			if (excluding && row.studentId === excluding.studentId && slotIndex === excluding.slotIndex) continue
			const cell = row.cells[slotIndex]
			if ((cell.state === 'correct' || cell.state === 'incorrect') && !cell.manuallyGraded) {
				return { studentId: row.studentId, slotIndex }
			}
		}
	}
	return null
}

export const GradesTable = ({ view, rows, slotPercents }: { view: ViewMode; rows: StudentRow[]; slotPercents: SlotPercent[] }) => {
	const { slots, gradeCell } = useGradesData()
	const [target, setTarget] = useState<GradeTarget | null>(null)
	const targetRow = target ? rows.find(r => r.studentId === target.studentId) : undefined

	if (!rows.length) {
		return <div className={styles.card}><p className={styles.empty}>No students at this level.</p></div>
	}

	const lowestPct = Math.min(...slotPercents.filter(s => s.pct !== null).map(s => s.pct as number))

	const openCell = (row: StudentRow, slotIndex: number) => {
		const cell = row.cells[slotIndex]
		if (cell.state !== 'correct' && cell.state !== 'incorrect') return
		setTarget({ studentId: row.studentId, slotIndex })
	}

	return (
		<div className={styles.card}>
			<div className={styles.scroll}>
				<table>
					{view === 'list' ? (
						<>
							<thead>
								<tr>
									<th>Student</th>
									<th>Level</th>
									<th>Questions graded</th>
									<th>Overall</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row, i) => (
									<tr key={row.studentId}>
										<td className={styles.who}>
											<div className={styles.whoCell}><span className={styles.rn}>{i + 1}</span><span className={styles.nm}>{row.studentName}</span></div>
										</td>
										<td><TierTag tier={row.tier} /></td>
										<td>{row.gradedCount} / {slots.length} graded</td>
										<td className={styles.score}>
											{row.overallPct === null ? '—' : `${row.overallPct}%`}
											{row.gradedCount < slots.length && <span className={styles.partial}>of {row.gradedCount} graded</span>}
										</td>
									</tr>
								))}
							</tbody>
						</>
					) : (
						<>
							<thead>
								<tr>
									<th>Student</th>
									<th>Level</th>
									{slots.map((slot, i) => {
										const pct = slotPercents[i]?.pct
										const hard = pct !== null && pct === lowestPct && pct < 100
										return (
											<th key={slot.slotKey} className={`${styles.qcol} ${hard ? styles.qcolHard : ''}`}>
												<span className={styles.qnum}>Q{i + 1}</span>
												<span className={styles.qpct}>{pct === null || pct === undefined ? '—' : `${pct}%`}</span>
											</th>
										)
									})}
									<th style={{ textAlign: 'right' }}>Overall</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row, i) => (
									<tr key={row.studentId}>
										<td className={styles.who}>
											<div className={styles.whoCell}><span className={styles.rn}>{i + 1}</span><span className={styles.nm}>{row.studentName}</span></div>
										</td>
										<td><TierTag tier={row.tier} /></td>
										{row.cells.map((cell, slotIndex) => (
											<td key={slotIndex} className={styles.cell}>
												<GradeMark cell={cell} onClick={() => openCell(row, slotIndex)} label={`Q${slotIndex + 1}`} />
											</td>
										))}
										<td className={styles.score} style={{ textAlign: 'right' }}>
											{row.overallPct === null ? '—' : `${row.overallPct}%`}
											{row.gradedCount < slots.length && <span className={styles.partial}>of {row.gradedCount} graded</span>}
										</td>
									</tr>
								))}
							</tbody>
						</>
					)}
				</table>
			</div>
			{view === 'grid' && (
				<div className={styles.legend}>
					<span><span className={`${styles.mark} ${styles.ok}`}>✓</span> Correct</span>
					<span><span className={`${styles.mark} ${styles.no}`}>✕</span> Incorrect</span>
					<span><span className={`${styles.mark} ${styles.ungraded}`}>–</span> No submission yet</span>
					<span className={styles.legendNote}>Column % = how much of the class got that question right</span>
				</div>
			)}
			{target && targetRow && (
				<GradeModal
					target={target}
					row={targetRow}
					totalSlots={slots.length}
					onClose={() => setTarget(null)}
					onMark={correct => {
						const wasUnreviewed = !targetRow.cells[target.slotIndex].manuallyGraded
						gradeCell(target.studentId, target.slotIndex, correct)
						// Same shape as the mockup's setMark: clearing an outstanding
						// item jumps straight to the next one instead of sitting on
						// the one just finished.
						if (wasUnreviewed) {
							const next = findFirstUnreviewed(rows, target)
							if (next) setTarget(next)
						}
					}}
					onNextUnreviewed={() => setTarget(findFirstUnreviewed(rows))}
					hasUnreviewed={!!findFirstUnreviewed(rows, target)}
				/>
			)}
		</div>
	)
}

const GradeMark = ({ cell, onClick, label }: { cell: GradeCell; onClick: () => void; label: string }) => {
	if (cell.state === 'correct') {
		return <button type="button" className={`${styles.mark} ${styles.ok}`} title={`${label}: correct — click to review`} onClick={onClick}>✓</button>
	}
	if (cell.state === 'incorrect') {
		return <button type="button" className={`${styles.mark} ${styles.no}`} title={`${label}: incorrect — click to review`} onClick={onClick}>✕</button>
	}
	const title = cell.state === 'not-submitted' ? `${label}: not yet submitted` : `${label}: not part of this student's lesson`
	return <span className={`${styles.mark} ${styles.ungraded}`} title={title} aria-label={title}>–</span>
}

const LETTERS = 'ABCDEFGH'

const GradeModal = ({ target, row, totalSlots, onClose, onMark, onNextUnreviewed, hasUnreviewed }: {
	target: GradeTarget
	row: StudentRow
	totalSlots: number
	onClose: () => void
	onMark: (correct: boolean) => void
	onNextUnreviewed: () => void
	hasUnreviewed: boolean
}) => {
	const cell = row.cells[target.slotIndex]
	const isCorrect = cell.state === 'correct'
	// options.indexOf against the student's stored answer text -- there is no
	// stored answer INDEX, only the text EduQuery recorded at grading time.
	const pickedIndex = cell.options ? cell.options.indexOf(cell.studentAnswer || '') : -1
	const correctIndex = cell.options ? cell.options.indexOf(cell.correctAnswer || '') : -1

	/* The mockup's grading panel locks the page behind it, puts focus on
	   Correct, and hands focus back on close -- `openGrade`/`closeGrade` in
	   AllForOne_Grades_Grid.html. None of that existed in any modal in this
	   app. It lives in one place now rather than being inlined here, because
	   the next modal would otherwise have to re-discover it. Escape stays
	   below: that is this dialog's own key handling, not a window-level
	   side effect. */
	const markRightRef = React.useRef<HTMLButtonElement>(null)
	useModalBehaviour(markRightRef)

	React.useEffect(() => {
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [onClose])

	return (
		<div className={styles.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
			<div className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="gradeTitle">
				<div className={styles.panelHead}>
					<div>
						<h2 id="gradeTitle">Question {target.slotIndex + 1} of {totalSlots}</h2>
						<div className={styles.who2}>{row.studentName} · {row.tier}</div>
					</div>
					<button type="button" className={styles.iconBtn} aria-label="Close" onClick={onClose}>
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
					</button>
				</div>
				<div className={styles.panelBody}>
					<div className={styles.qLabel}>The question</div>
					<p className={styles.qBody}>{cell.questionText}</p>
					{cell.options ? (
						cell.options.map((opt, i) => {
							const isRight = i === correctIndex
							const isPicked = i === pickedIndex
							const flag = isPicked && isRight ? 'Their answer · correct'
								: isPicked ? 'Their answer'
								: isRight ? 'Correct answer' : null
							// Picked-and-correct reads as "correct" (mint), not "picked"
							// (terracotta) -- matches the mockup's .opt.picked.correct
							// override, which always wins over plain .opt.picked.
							return (
								<div
									key={i}
									className={`${styles.opt} ${isRight ? styles.optCorrect : (isPicked ? styles.optAnswer : '')}`}
								>
									<span className={styles.letter}>{LETTERS.charAt(i)}</span>
									{opt}
									{flag && (
										<span className={`${styles.flag} ${isPicked && !isRight ? styles.flagPicked : styles.flagOk}`}>{flag}</span>
									)}
								</div>
							)
						})
					) : (
						<>
							<div className={`${styles.opt} ${styles.optAnswer}`}>
								<span className={styles.letter}>A</span>
								{cell.studentAnswer}
								<span className={`${styles.flag} ${isCorrect ? styles.flagOk : styles.flagPicked}`}>Their answer{isCorrect ? ' · correct' : ''}</span>
							</div>
							{!isCorrect && cell.correctAnswer && (
								<div className={`${styles.opt} ${styles.optCorrect}`}>
									<span className={styles.letter}>B</span>
									{cell.correctAnswer}
									<span className={`${styles.flag} ${styles.flagOk}`}>Correct answer</span>
								</div>
							)}
						</>
					)}
				</div>
				<div className={styles.panelFoot}>
					<button ref={markRightRef} type="button" className={`${styles.btnMark} ${styles.right} ${isCorrect ? styles.on : ''}`} onClick={() => onMark(true)}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
						Correct
					</button>
					<button type="button" className={`${styles.btnMark} ${styles.wrong} ${!isCorrect ? styles.on : ''}`} onClick={() => onMark(false)}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
						Incorrect
					</button>
					{/* Mockup's status reads "Not yet graded"/"Graded" -- every cell here
					    already carries an AI grade, so that would be a false claim. This
					    is the honest equivalent: whether a teacher has confirmed it. */}
					<span className={`${styles.panelNav} ${styles.spacer}`}>{cell.manuallyGraded ? 'Reviewed' : 'Not yet reviewed'}</span>
					<button type="button" className={styles.btnNextUngraded} disabled={!hasUnreviewed} onClick={onNextUnreviewed}>
						Next ungraded
					</button>
				</div>
			</div>
		</div>
	)
}
